/**
 * Console Capture
 *
 * Monkey-patches console.error / console.warn into a small capped ring buffer
 * so the bug reporter can attach recent client-side errors. Call
 * initConsoleCapture() once at app startup (idempotent).
 */

import type { ConsoleLogEntry, BugSystemInfo } from '@machina/core';

const MAX_ENTRIES = 50;
const buffer: ConsoleLogEntry[] = [];
let initialized = false;

function push(level: ConsoleLogEntry['level'], args: unknown[]) {
  const message = args
    .map((a) => {
      if (typeof a === 'string') return a;
      if (a instanceof Error) return `${a.name}: ${a.message}`;
      try {
        return JSON.stringify(a);
      } catch {
        return String(a);
      }
    })
    .join(' ')
    .slice(0, 2000);

  buffer.push({ level, message, timestamp: new Date().toISOString() });
  if (buffer.length > MAX_ENTRIES) buffer.shift();
}

export function initConsoleCapture(): void {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  const originalError = console.error.bind(console);
  const originalWarn = console.warn.bind(console);

  console.error = (...args: unknown[]) => {
    push('error', args);
    originalError(...args);
  };
  console.warn = (...args: unknown[]) => {
    push('warn', args);
    originalWarn(...args);
  };

  // Also capture uncaught errors + unhandled rejections.
  window.addEventListener('error', (e) => {
    push('error', [e.message, e.filename ? `(${e.filename}:${e.lineno})` : '']);
  });
  window.addEventListener('unhandledrejection', (e) => {
    push('error', ['Unhandled rejection:', e.reason]);
  });
}

/** Snapshot of the captured logs (oldest -> newest). */
export function getCapturedLogs(): ConsoleLogEntry[] {
  return [...buffer];
}

/** Best-effort browser / environment info for triage. */
export function getSystemInfo(): BugSystemInfo {
  if (typeof window === 'undefined') return {};
  return {
    browser: navigator.userAgent,
    userAgent: navigator.userAgent,
    route: window.location.pathname + window.location.search,
    screen: `${window.screen.width}x${window.screen.height}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    language: navigator.language,
    platform: navigator.platform,
    timestamp: new Date().toISOString(),
  };
}
